import { useState, useEffect } from "react";
import { CalendarIcon } from "lucide-react";
import { format, isValid } from "date-fns";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CalendarHeader } from "@/components/settings/profile/CalendarHeader";

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
  className?: string;
  placeholder?: string;
  id?: string;
}

// 🔧 Función auxiliar para crear fecha en hora local (evita problema de timezone)
const toLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day); // mes 0-indexado
};

export const DatePicker = ({
  value,
  onChange,
  required,
  disabled = false,
  minYear = 1920,
  maxYear = new Date().getFullYear() + 10,
  className = "",
  placeholder = "YYYY-MM-DD",
  id = "date",
}: DatePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value && isValid(toLocalDate(value)) ? toLocalDate(value) : undefined
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    value && isValid(toLocalDate(value)) ? toLocalDate(value) : new Date()
  );

  useEffect(() => {
    if (value && isValid(toLocalDate(value))) {
      const localDate = toLocalDate(value);
      setSelectedDate(localDate);
      setCalendarMonth(localDate);
    } else {
      setSelectedDate(undefined);
    }
  }, [value]);

  const handleCalendarSelect = (date?: Date) => {
    if (date) {
      // Format date manually to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      onChange(dateString);
      setSelectedDate(date);
      setCalendarMonth(date);
      setCalendarOpen(false);
    }
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onChange(value);
    if (value && isValid(toLocalDate(value))) {
      const newDate = toLocalDate(value);
      setSelectedDate(newDate);
      setCalendarMonth(newDate);
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleDateFieldClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCalendarOpen(true);
  };

  const handleCalendarMonthChange = (newMonth: Date) => {
    setCalendarMonth(newMonth);
  };

  return (
    <div className={cn("relative flex flex-col space-y-1", className)}>
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <div className="relative flex items-center text-white">
            <Input
              id={id}
              type="text"
              inputMode="none"
              autoComplete="off"
              pattern="\d{4}-\d{2}-\d{2}"
              placeholder={placeholder}
              value={value}
              onChange={handleDateInput}
              required={required}
              disabled={disabled}
              className={cn(
                "pr-10 cursor-pointer text-black dark:text-white ocean:text-white forest:text-white sunset:text-white forest:bg-[#122112] ocean:bg-[#081F2B] bg-white dark:bg-[#293233] border border-[#00C1D6]/30",
                !value && "text-muted-foreground"
              )}
              onClick={handleDateFieldClick}
              readOnly
            />
            <CalendarIcon
              className="absolute right-3 text-muted-foreground h-5 w-5 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setCalendarOpen((v) => !v);
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent align="center" className="p-0 z-50 w-auto bg-black/50" sideOffset={8}>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            month={calendarMonth}
            onMonthChange={handleCalendarMonthChange}
            fromYear={minYear}
            toYear={maxYear}
            components={{
              Caption: (props) => (
                <CalendarHeader
                  {...props}
                  fromYear={minYear}
                  toYear={maxYear}
                  onMonthChange={handleCalendarMonthChange}
                />
              ),
            }}
            disabled={(date) =>
              (maxYear < new Date().getFullYear() && date > new Date()) || 
              date < new Date(`${minYear}-01-01`)
            }
            className="rounded-lg border p-2 bg-black/70 text-white pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
